/**
 * ZITELOU - Dashboard Page
 * Page principale avec statistiques et graphiques
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  CurrencyEuroIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Card, Badge, Loader } from '../components/Common';
import StatCard from '../components/Dashboard/StatCard';
import LineChart from '../components/Dashboard/LineChart';
import BarChart from '../components/Dashboard/BarChart';
import PieChart from '../components/Dashboard/PieChart';
import api from '../services/api';
import { formatCurrency, formatDate, formatRelativeTime } from '../utils';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await api.getDashboardData();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader fullScreen text="Chargement du tableau de bord..." />;
  }

  const { stats, recentUsers, recentTransactions, growthData, activityData } = dashboardData;

  // Prepare chart data
  const growthChartData = {
    labels: growthData.map((d) => d.month),
    datasets: [
      {
        label: 'Utilisateurs',
        data: growthData.map((d) => d.users),
        borderColor: 'rgb(1, 57, 134)',
        backgroundColor: 'rgba(1, 57, 134, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Abonnements',
        data: growthData.map((d) => d.subscriptions),
        borderColor: 'rgb(150, 203, 132)',
        backgroundColor: 'rgba(150, 203, 132, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const activityChartData = {
    labels: activityData.map((d) => formatDate(d.date, 'fr-FR', { day: '2-digit', month: 'short' })),
    datasets: [
      {
        label: 'Nouveaux',
        data: activityData.map((d) => d.new_users),
        backgroundColor: 'rgba(1, 57, 134, 0.8)',
      },
      {
        label: 'Résiliés',
        data: activityData.map((d) => d.cancelled),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
      },
    ],
  };

  const subscriptionTypesData = {
    labels: ['Mensuel', 'Annuel'],
    datasets: [
      {
        data: [stats.monthly_subscriptions, stats.yearly_subscriptions],
        backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)'],
        borderColor: ['rgb(59, 130, 246)', 'rgb(16, 185, 129)'],
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-gray-600 mt-2">
          Vue d'ensemble de votre plateforme Zitelou
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Utilisateurs"
          value={stats.total_users.toLocaleString('fr-FR')}
          change={`+${stats.new_users_30d}`}
          changeType="positive"
          icon={UsersIcon}
          iconBgColor="bg-primary/20"
          iconColor="text-primary"
        />

        <StatCard
          title="Abonnés Actifs"
          value={stats.active_subscriptions.toLocaleString('fr-FR')}
          change={`${stats.monthly_growth > 0 ? '+' : ''}${stats.monthly_growth}%`}
          changeType={stats.monthly_growth > 0 ? 'positive' : 'negative'}
          icon={CreditCardIcon}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />

        <StatCard
          title="Croissance"
          value={`${stats.monthly_growth}%`}
          change={`Taux: ${stats.conversion_rate}%`}
          changeType="positive"
          icon={ArrowTrendingUpIcon}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />

        <StatCard
          title="MRR"
          value={formatCurrency(stats.mrr)}
          change={`${stats.cancelled_30d} résiliations`}
          changeType="neutral"
          icon={CurrencyEuroIcon}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growth Chart */}
        <Card title="Évolution des Abonnements" subtitle="12 derniers mois">
          <LineChart data={growthChartData} height={300} />
        </Card>

        {/* Activity Chart */}
        <Card title="Activité Récente" subtitle="7 derniers jours">
          <BarChart data={activityChartData} height={300} />
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Types */}
        <Card title="Répartition Abonnements" subtitle="Par type">
          <PieChart data={subscriptionTypesData} height={250} />
        </Card>

        {/* Recent Users */}
        <Card
          title="Derniers Utilisateurs"
          subtitle={`${recentUsers.length} utilisateurs récents`}
          headerAction={
            <Link to="/users" className="text-sm text-primary hover:opacity-80 font-medium">
              Voir tout
            </Link>
          }
        >
          <div className="space-y-3">
            {recentUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.firstname} {user.lastname}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <div className="ml-3 flex items-center gap-2">
                  <Badge status={user.subscription_status} size="sm" />
                  <Link to={`/users/${user.id}`}>
                    <button className="p-1 hover:bg-gray-200 rounded">
                      <EyeIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Transactions */}
        <Card
          title="Dernières Transactions"
          subtitle={`${recentTransactions.length} transactions récentes`}
          headerAction={
            <Link to="/subscriptions" className="text-sm text-primary hover:opacity-80 font-medium">
              Voir tout
            </Link>
          }
        >
          <div className="space-y-3">
            {recentTransactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {tx.user_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatRelativeTime(tx.date)}
                  </p>
                </div>
                <div className="ml-3 flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(tx.amount)}
                  </p>
                  <Badge
                    status={tx.status === 'succeeded' ? 'success' : 'error'}
                    size="sm"
                  >
                    {tx.status === 'succeeded' ? '✓' : '✗'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
